import Autocomplete, { type AutocompleteProps } from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { forwardRef } from 'react';

import { Text } from '../../text';
import type { ItemComponentProps, OptionSelectionProps } from '../types';

type AutocompleteSlotProps = AutocompleteProps<
  OptionSelectionProps,
  true,
  boolean,
  false
>['slotProps'];

type SearchMultipleSelectionProps = Omit<
  AutocompleteProps<OptionSelectionProps, true, boolean, false>,
  'renderInput' | 'slotProps'
> &
  Pick<
    TextFieldProps,
    | 'label'
    | 'error'
    | 'helperText'
    | 'required'
    | 'variant'
    | 'color'
    | 'inputRef'
    | 'InputProps'
    | 'placeholder'
  > & {
    itemComponent?: React.FC<ItemComponentProps>;
    // Autocomplete slot props (popper, listbox, paper, …) plus the TextField `input`
    // slot this wrapper applies to its rendered input.
    slotProps?: AutocompleteSlotProps & {
      input?: NonNullable<TextFieldProps['slotProps']>['input'];
    };
  };

function DefaultItem(props: Readonly<ItemComponentProps>) {
  const { option } = props;
  return <Text>{option.label}</Text>;
}

const SearchMultipleSelection = forwardRef((props: Readonly<SearchMultipleSelectionProps>, ref) => {
  const {
    placeholder,
    helperText,
    error: hasError,
    label,
    options,
    required: isRequired,
    variant,
    color,
    InputProps,
    inputRef,
    itemComponent: ItemComponent = DefaultItem,
    value,
    slotProps,
    ...restProps
  } = props;

  const { input: inputSlotProps, ...autocompleteSlotProps } = slotProps ?? {};

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      ref={ref}
      options={options ?? []}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ],
        },
        ...autocompleteSlotProps,
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={hasError}
          helperText={helperText}
          required={isRequired}
          variant={variant}
          color={color}
          inputRef={inputRef}
          slotProps={{
            input: { ...params.InputProps, ...InputProps, ...inputSlotProps },
          }}
        />
      )}
      renderOption={({ key, onClick, ...restOptionProps }, option, { selected }) => {
        return (
          <MenuItem
            key={key}
            {...restOptionProps}
            value={option.value}
            onClick={(event) => {
              // A custom itemComponent (e.g. a checkbox) may render a native
              // <label>, whose default click behavior forwards the click to
              // its hidden <input> and focuses it. That focus jump — away
              // from the Autocomplete's own input — defeats MUI's click-
              // inside-listbox blur guard and closes the popup even with
              // disableCloseOnSelect set. Suppressing the browser default
              // blocks that forwarding while still letting this same click
              // select the option normally.
              event.preventDefault();
              onClick?.(event);
            }}
          >
            <ItemComponent option={option} selected={selected} />
          </MenuItem>
        );
      }}
      //FIXME:value is possibly to be undefined
      value={value ?? []}
      {...restProps}
    />
  );
});

SearchMultipleSelection.displayName = 'SearchMultipleSelection';

export type { SearchMultipleSelectionProps };
export { SearchMultipleSelection };
